<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="
      isCRDetailsPending
        ? submitButton
        : user?.evaluation_details?.status_cd == 'awaiting_approval'
          ? approveCRButton
          : {}
    "
    :secondary-cta="{}"
    :ternary-cta="
      user?.evaluation_details?.status_cd == 'awaiting_approval'
        ? rejectCRButton
        : {}
    "
    body-class="q-px-2x-md q-pt-xl"
    @submit.prevent
  >
    <div
      v-if="
        user?.background_check?.direct_indirect_grading_status_cd != 'completed'
      "
    >
      <div
        class="col-12 flex items-center bg-warning-20 q-py-sm q-px-xs rounded-borders"
      >
        <IncompleteIcon class="icon text-warning-70 q-mr-xs" />
        <div>
          <span class="text-sm text-neutral text-weight-medium q-mr-xs">
            Incomplete Verification
          </span>

          <span class="text-xs text-neutral-80">
            Complete to generate credit evaluation summary
          </span>
        </div>
      </div>
      <PBtn
        flat
        class="flex q-ml-auto q-pa-none"
        :disable="isCompleteNowDisabled"
        @click="openVerificationCenter"
      >
        <StartProcess class="icon--sm q-mr-xs" />
        <span class="text-sm text-weight-medium">Complete now</span>
        <PTooltip v-if="isCompleteNowDisabled">
          Fill mandatory fields
        </PTooltip>
      </PBtn>
      <q-separator color="neutral-40" class="q-mt-xs" />
    </div>
    <div
      class="col-12"
      :class="
        user?.background_check?.direct_indirect_grading_status_cd != 'completed'
          ? 'q-mt-md'
          : ''
      "
    >
      <div class="flex items-center">
        <span class="text-lg text-weight-medium text-neutral">
          Credit Evaluation Summary
        </span>
        <InfoPopover v-if="!isCRDetailsPending" :info="approvalInfo">
          <PBadge
            v-if="computedEvaluationDetailsStatus"
            class="q-ml-xs"
            :label="computedEvaluationDetailsStatus"
            :type="
              user?.evaluation_details?.status_cd == 'approved'
                ? 'completed'
                : user?.evaluation_details?.status_cd
            "
          />
        </InfoPopover>
      </div>

      <div
        v-if="
          user?.background_check?.direct_indirect_grading_status_cd ==
          'completed'
        "
        class="flex items-center justify-between q-mt-md"
      >
        <PBtn
          flat
          class="q-pa-none"
          label="View evaluation timeline"
          @click="openTimelines"
        />
        <PBtn
          flat
          class="q-pa-none"
          label="View verification details"
          @click="openVerificationCenter"
        />
      </div>
      <q-separator color="neutral-40" class="q-mt-xs" />
    </div>
    <div class="col-12 q-mt-xl">
      <span class="text-md text-weight-medium text-neutral"> Business </span>
      <q-separator color="neutral-40" class="q-mt-xxs" />
    </div>

    <div class="row q-col-gutter-x-xxl q-mt-sm">
      <div class="col-6">
        <div class="text-sm text-neutral-80">Registered Business Name</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{ computedBusinessDetails?.shop_name || "-" }}
        </div>
      </div>
      <div class="col-6">
        <div class="text-sm text-neutral-80">Annual Turnover</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{
            computedBusinessDetails?.aggregate_turnover_range != null &&
            computedBusinessDetails?.aggregate_turnover_range?.minimum !=
              null &&
            computedBusinessDetails?.aggregate_turnover_range?.maximum != null
              ? computedBusinessDetails?.aggregate_turnover_range?.maximum != 0
                ? `Rs. ${computedBusinessDetails?.aggregate_turnover_range?.minimum} to Rs. ${computedBusinessDetails?.aggregate_turnover_range?.maximum}`
                : "-"
              : "-"
          }}
        </div>
      </div>
    </div>
    <div class="row q-col-gutter-x-xxl q-mt-sm">
      <div class="col-6">
        <div class="text-sm text-neutral-80">GSTIN</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{ computedBusinessDetails?.gstin || "Unregistered" }}
        </div>
      </div>
      <div class="col-6">
        <div class="text-sm text-neutral-80">Filing Status</div>
        <div class="text-sm text-neutral text-weight-medium text-capitalize">
          {{ computedBusinessDetails?.filing_frequency || "-" }}
        </div>
      </div>
    </div>
    <div class="row q-col-gutter-x-xxl q-mt-sm">
      <div class="col-6">
        <div class="text-sm text-neutral-80">GSTIN Status</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{ computedBusinessDetails?.gstin_status || "-" }}
        </div>
      </div>
      <div class="col-6">
        <div class="text-sm text-neutral-80">Date of Registration</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{
            formatDate(
              computedBusinessDetails?.registration_date,
              "D MMM YYYY",
            ) || "-"
          }}
        </div>
      </div>
    </div>
    <div class="row q-col-gutter-x-xxl q-mt-sm">
      <div class="col-6">
        <div class="text-sm text-neutral-80">Registered Address</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{
            computedBusinessDetails?.address?.[0] +
              ", " +
              computedBusinessDetails?.pincode || "-"
          }}
        </div>
      </div>
      <div class="col-6">
        <div class="text-sm text-neutral-80">Date of Incorporation</div>
        <div class="text-sm text-neutral text-weight-medium">
          {{
            formatDate(
              computedBusinessDetails?.incorporation_date,
              "D MMM YYYY",
            ) || "-"
          }}
        </div>
      </div>
    </div>

    <div class="col-12 q-mt-2x-xl">
      <span class="text-md text-weight-medium text-neutral">
        Director’s Credit Summary
      </span>
      <q-separator color="neutral-40" class="q-mt-xxs" />
    </div>
    <div
      v-if="computedBusinessDetails?.business_type?.code != 'PLC'"
      class="q-ma-xs"
    >
      <PTable
        :columns="directorListColumns"
        :pagination="{
          rowsPerPage: computedBusinessDetails?.directors?.length,
        }"
        :rows="computedBusinessDetails?.directors"
        :style="{ margin: '0 -15px', padding: 0 }"
        class="text-xs"
        table-header-class="text-neutral-70"
        dense
        flat
        wrap-cells
        hide-bottom
      >
        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <div class="q-py-sm">
              <div class="text-xs text-neutral text-capitalize">
                {{ props.row.name || "-" }}
              </div>
              <div class="text-xxs text-neutral-70">
                {{ props.row.pan || "-" }}
              </div>
            </div>
          </q-td>
        </template>

        <template #body-cell-credit_score="props">
          <q-td :key="props.col.name" :props="props">
            <div class="text-xs text-neutral">{{ props.value || "-" }}</div>
          </q-td>
        </template>

        <template #body-cell-delinquencies="props">
          <q-td :key="props.col.name" :props="props">
            <div class="text-xs text-neutral">
              {{ props.value || 0 }}
            </div>
          </q-td>
        </template>

        <template #body-cell-balance="props">
          <q-td :key="props.col.name" :props="props">
            <div class="text-xs text-neutral">
              {{
                numberFormat({
                  number: props.value || 0,
                  style: "currency",
                })
              }}
            </div>
          </q-td>
        </template>

        <template #body-cell-overdraft="props">
          <q-td :key="props.col.name" :props="props">
            <div v-if="props.value?.length">
              <InfoPopover :info="overdraftInfo(props.value)">
                <span
                  class="text-xs text-primary text-weight-medium cursor-pointer"
                >
                  {{ props.value.length }}
                </span>
              </InfoPopover>
            </div>
            <PBadge v-else :label="'NA'" :type="'NA'" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <PBtn
              v-if="props.row.credit_score"
              :to="`/users/${props.row._id}/credit-report`"
              class="q-ml-xs"
              target="_blank"
              dense
              flat
              rounded
            >
              <ViewIcon class="icon--md" />
              <PTooltip> View Details </PTooltip>
            </PBtn>
            <PBtn
              v-else
              dense
              flat
              rounded
              class="q-ml-xs"
              @click="editDirector(props.row)"
            >
              <EditIcon class="icon--md" />
              <PTooltip> Edit detail </PTooltip>
            </PBtn>
          </q-td>
        </template>
      </PTable>
    </div>
    <div v-else class="q-ma-xs">Not Applicable</div>

    <div class="col-12 q-mt-2x-xl">
      <span class="text-md text-weight-medium text-neutral">
        Company’s Credit Rating
      </span>
      <q-separator color="neutral-40" class="q-mt-xxs" />
    </div>
    <div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-6">
          <div class="text-sm text-neutral q-mt-xs">
            Do you want to add credit rating?
          </div>
        </div>
        <div class="col-6">
          <PField v-model="isCreditRatingsApplicable">
            <template #control>
              <POptionGroup
                v-model="isCreditRatingsApplicable"
                :disable="
                  !isCRDetailsPending ||
                  computedBusinessDetails?.business_type?.code == 'PLC'
                "
                :options="[
                  { label: 'No', value: false },
                  { label: 'Yes', value: true },
                ]"
                @update:model-value="onUpdateCreditRatingApplicable"
              />
            </template>
          </PField>
        </div>
      </div>
      <div class="col-12">
        <div v-for="(rating, index) in formData.credit_ratings" :key="index">
          <div class="row q-col-gutter-x-xxl q-mt-sm">
            <div class="col-6">
              <PSelectInput
                v-model="rating.agency"
                :options="filteredAgencyOptions"
                label="Credit Rating Agency"
                :disable="computedDisableCreditRating"
                placeholder="Select rating agency"
                :clearable="false"
                @update:model-value="(agency) => updateAgency(agency, index)"
              />
            </div>
            <div class="col-6">
              <div class="row q-col-gutter-x-xs">
                <div class="col">
                  <PSelectInput
                    v-model="rating.rating"
                    :options="availableGrades(rating.agency)"
                    :option-label="formatRatingLabel"
                    :option-value="formatRatingValue"
                    :label="
                      (rating?.agency ? rating.agency : '{platform name}') +
                      ' Rating'
                    "
                    :disable="computedDisableCreditRating"
                    placeholder="Select rating"
                    :clearable="false"
                    @update:model-value="
                      (selectedRating) => updateRatings(selectedRating, index)
                    "
                  >
                    <template #option="scope">
                      <q-item v-bind="scope.itemProps">
                        <q-item-section>
                          <q-item-label>{{
                            scope.opt.grade +
                            (scope.opt.indicator
                              ? ` (${scope.opt.indicator})`
                              : "")
                          }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                  </PSelectInput>
                </div>
                <div v-if="index > 0" class="flex items-center">
                  <PBtn
                    dense
                    flat
                    rounded
                    class="q-mt-xl"
                    :disable="
                      computedDisableCreditRating ||
                      isCreditUpdateLoading[index]
                    "
                    @click="handleCreditRatingUpdate(index, true)"
                  >
                    <RemoveIcon class="icon--md" />
                    <PTooltip> Remove {{ rating.agency }} </PTooltip>
                  </PBtn>
                </div>
                <div v-else class="q-ml-xxl" />
              </div>
            </div>
          </div>
          <div
            v-if="isCreditRatingsApplicable"
            class="row q-col-gutter-x-xxl q-mt-sm"
          >
            <div class="col-6">
              <PInput
                v-model="rating.rating_source_link"
                label="Rating Source Link"
                placeholder="Enter rating source link"
                required
                debounce="500"
                @update:model-value="handleRatingMetadataUpdate(index)"
              />
            </div>
            <div class="col-6">
              <div class="row q-col-gutter-x-xs">
                <div class="col">
                  <PVDateInput
                    v-model="rating.rating_added_date"
                    label="Added On"
                    placeholder="DD-MM-YYYY"
                    required
                    :max-date="currentDate"
                    :loading="isCreditUpdateLoading[index]"
                    @update:model-value="handleRatingMetadataUpdate(index)"
                  />
                </div>
                <div class="q-ml-xxl" />
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <PBtn
            dense
            flat
            fluid
            class="flex q-mr-auto q-mt-md"
            :disable="
              computedDisableCreditRating ||
              agencyOptions?.length == formData.credit_ratings?.length ||
              isAnyCreditUpdateLoading
            "
            @click="addMoreRating"
          >
            <AddIcon class="icon--sm q-mr-sm" />
            <span>Add More Rating Agency</span>
          </PBtn>
        </div>
      </div>
      <div class="row q-col-gutter-x-xxl q-mt-lg">
        <div class="col-5">
          <div class="text-sm text-neutral q-mt-xs">
            Legal Cases
            <span class="text-negative">*</span>
          </div>
        </div>
        <div class="col-7">
          <div class="flex items-center">
            <PSelectInput
              v-model="formData.legal_cases"
              :options="unref(legalCasesOptions)"
              option-label="name"
              option-value="value"
              class="col"
              placeholder="Select legal case option"
              required
              :disable="!isCRDetailsPending"
              @update:model-value="handleLegalCaseChange"
            />
            <PBtn
              :to="`/users/${user?._id}/court-cases-report`"
              class="q-mb-lg q-ml-xs"
              target="_blank"
              dense
              flat
              rounded
            >
              <ViewIcon class="icon--md" />
              <PTooltip> View Report </PTooltip>
            </PBtn>
          </div>
        </div>
      </div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-5">
          <div class="text-sm text-neutral">Negative Due Diligence</div>
          <div class="text-xs text-neutral-70">
            Enhanced Risk Assessment & Compliance
          </div>
        </div>
        <div class="col-7">
          <div class="flex items-center">
            <PInput
              v-model.number="formData.negative_due_diligence"
              :disable="!isCRDetailsPending"
              :validations="{ gte: 0, lte: 100 }"
              class="col"
              placeholder="Enter negative due diligence"
              type="number"
              required
            />
            <PBtn
              :to="`/users/${user?._id}/due-diligence-report`"
              class="q-mb-lg q-ml-xs"
              target="_blank"
              dense
              flat
              rounded
            >
              <ViewIcon class="icon--md" />
              <PTooltip> View Report </PTooltip>
            </PBtn>
          </div>
        </div>
      </div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-5">
          <div class="text-sm text-neutral">Direct / Indirect Grade</div>
          <div class="text-xs text-neutral-70">
            Autofilled based on Credit Report Verification
          </div>
        </div>
        <div class="col-7">
          <PInput
            v-model="formData.direct_indirect_grade"
            placeholder="Autofilled"
            :hint="
              formData?.direct_indirect_grade?.includes('*')
                ? 'Note: * indicates that stage is skipped'
                : ''
            "
            disable
            required
          />
        </div>
      </div>
    </div>

    <div class="col-12 q-mt-2x-xl">
      <span class="text-md text-weight-medium text-neutral"> Outlook </span>
      <q-separator color="neutral-40" class="q-mt-xxs" />
    </div>

    <div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-5">
          <div class="text-sm text-neutral q-mt-xs">
            Eligible for {{ user.role == "buyer" ? "Credit" : "Advance" }}
          </div>
        </div>
        <div class="col-7">
          <PSelectInput
            v-model="computedExposureEligibility"
            placeholder="Select an option"
            :options="unref(eligibilityOptions)"
            option-label="name"
            option-value="code"
            :clearable="false"
          />
        </div>
      </div>
      <div class="row q-col-gutter-x-xxl q-mt-xxl">
        <div class="col-5">
          <div class="text-sm text-neutral q-mt-xs">Exposure Limit</div>
        </div>
        <div class="col-7">
          <PInput
            v-model.number="formData.credit_limit"
            :disable="!formData?.credit_eligibility?.value"
            placeholder="Autofilled"
            required
            type="number"
            class="prepend-bg"
          >
            <template #prepend>
              <span class="text-sm">₹</span>
            </template>
          </PInput>
        </div>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { date, useQuasar } from "quasar";

import IncompleteIcon from "assets/icons/exclamation-round.svg";
import StartProcess from "assets/icons/start_process_4.svg";
import ViewIcon from "assets/icons/view-detail.svg";
import EditIcon from "assets/icons/edit.svg";
import AddIcon from "assets/icons/add.svg";
import RemoveIcon from "assets/icons/remove.svg";

import creditEvaluationFormModel from "src/modules/user/models/creditEvaluationForm";
import UpdateCRStatusDialogForm from "./UpdateCRStatusDialogForm.vue";
import SendReportDialogForm from "./SendReportDialogForm.vue";
import { unref, watch } from "vue";
import { numberFormat } from "src/assets/scripts/utilities/helpers";
import { index as getCreditAgencies } from "src/api/credit-rating";
import { putLegalCaseStatus } from "src/api/user";

const { formatDate } = date;
const $q = useQuasar();

const pForm = ref(null);
const currentDate = new Date();

const { formState, user } = storeToRefs(useUserStore());
const {
  toggleVerificationCenterForm,
  toggleDirectorForm,
  putUserCreditRating,
  getUserCreditVerification,
  getUser,
} = useUserStore();
const { toggleTimelinesDrawer } = useAuditLogsStore();

const ratingAgencies = ref([]);
const isRefreshingCreditEvaluation = ref(false);

const computedBgCheckDetails = computed(() => {
  return unref(user)?.creditSummary?.background_check;
});

const { formData } = useForm({
  formModel: creditEvaluationFormModel(),
  entity: computedBgCheckDetails,
  formRef: pForm,
  isForm: formState,
});

const isCRDetailsPending = computed({
  get() {
    return (
      unref(user)?.evaluation_details?.status_cd == "pending" ||
      unref(user)?.evaluation_details?.status_cd == null
    );
  },
});

const openTimelines = () => {
  toggleTimelinesDrawer({
    open: true,
    level: 0,
    props: {
      entityId: unref(user)?._id,
      entityName: "CreditEvaluation",
      heading: "Evaluation Timeline",
    },
  });
};

// open verification center drawer
const openVerificationCenter = () => {
  toggleVerificationCenterForm({ open: true, edit: true, level: 0 });
};

const computedEvaluationDetailsStatus = computed({
  get() {
    const status = unref(user)?.evaluation_details?.status_cd;

    if (status == "awaiting_approval") {
      return "Awaiting Approval";
    } else if (status == "approved") {
      return "Approved";
    } else if (status == "rejected") {
      return "Rejected";
    }

    return "";
  },
});

const computedBusinessDetails = computed({
  get() {
    return unref(user)?.businessDetails;
  },
});

const directorListColumns = computed(() => [
  {
    align: "left",
    name: "name",
    label: "Director",
    field: (row) => row,
    headerClasses: "text-xs text-weight-medium text-neutral",
  },
  {
    align: "center",
    name: "credit_score",
    label: "CR Score",
    field: "credit_score",
    headerClasses: "text-xs text-weight-medium text-neutral",
  },
  {
    align: "center",
    name: "delinquencies",
    label: "Delinquents",
    field: "delinquencies",
    type: "currency",
    headerClasses: "text-xs text-weight-medium text-neutral",
  },
  {
    align: "center",
    name: "balance",
    label: "Balance",
    field: "balance",
    type: "currency",
    headerClasses: "text-xs text-weight-medium text-neutral",
  },
  {
    align: "center",
    name: "overdraft",
    label: "Overdraft",
    field: "overdraft",
    headerClasses: "text-xs text-weight-medium text-neutral",
  },
  {
    align: "left",
    classes: "sticky--right",
    headerClasses: "sticky--right bg-white",
    name: "action",
  },
]);

const hasValidCreditRatingSelected = computed(() => {
  return (
    unref(isCreditRatingsApplicable) &&
    Array.isArray(formData.value?.credit_ratings) &&
    formData.value.credit_ratings.some((rating) => hasValue(rating?.rating))
  );
});

const isDirectIndirectGradingCompleted = computed(() => {
  return (
    user.value?.background_check?.direct_indirect_grading_status_cd ===
    "completed"
  );
});

const isDirectIndirectCompleted = computed(() => {
  return (
    isDirectIndirectGradingCompleted.value &&
    !hasValidCreditRatingSelected.value
  );
});

const isCreditRatingsApplicable = ref(
  unref(computedBusinessDetails)?.business_type?.code == "PLC",
);

const isPopulatingSavedCreditRatings = ref(false);
const isCreditUpdateLoading = reactive({});

const handleCreditRatingUpdate = async (index, remove = false) => {
  try {
    isCreditUpdateLoading[index] = true;

    let payload = JSON.parse(JSON.stringify(formData.value.credit_ratings));

    if (remove) {
      payload = payload.filter((_, i) => i !== index);
    }

    const response = await putUserCreditRating(payload);

    if (isDirectIndirectGradingCompleted.value) {
      await refreshCreditEvaluationSummary();
    }

    if (response?.data?.data?.credit_ratings?.length) {
      user.value.credit_ratings = response?.data?.data?.credit_ratings;
    }

    isCreditUpdateLoading[index] = false;
  } catch (error) {
    isCreditUpdateLoading[index] = false;

    console.error(error);
  }
};

const isAnyCreditUpdateLoading = computed(() =>
  Object.values(isCreditUpdateLoading).includes(true),
);

const agencyOptions = computed(() => {
  return Object.keys(ratingAgencies.value);
});

const filteredAgencyOptions = computed(() => {
  const selectedAgencies = new Set(
    formData.value.credit_ratings.map((r) => r.agency),
  );

  return agencyOptions.value.map((option) => ({
    label: option,
    value: option,
    disable: selectedAgencies.has(option),
  }));
});

const availableGrades = (agencyKey) => {
  if (!agencyKey || !ratingAgencies.value || !ratingAgencies.value[agencyKey]) {
    return [];
  }

  return ratingAgencies.value[agencyKey];
};

const formatRatingLabel = (agency) => {
  const { grade, indicator } = agency;

  return grade + (indicator ? ` (${indicator})` : "");
};

const formatRatingValue = (selectedRating) => {
  const { grade, indicator, agency } = selectedRating;

  return agency + " " + grade + (indicator ? ` (${indicator})` : "");
};

const computedDisableCreditRating = computed(() => {
  return !isCRDetailsPending.value || !isCreditRatingsApplicable.value;
});

const updateAgency = async (agency, index) => {
  if (agency) {
    await nextTick();

    formData.value.credit_ratings[index].rating = null;
  }
};

const updateRatings = (selectedRating, index) => {
  if (!selectedRating) return;

  const selectedAgency = formData.value.credit_ratings[index].agency;

  const selectedRatingOption = ratingAgencies.value?.[selectedAgency]?.find(
    (rating) => {
      const label = formatRatingValue(rating);

      return label === selectedRating;
    },
  );

  formData.value.credit_ratings[index].ranking =
    selectedRatingOption?.ranking ?? null;
};

const handleRatingMetadataUpdate = async (index) => {
  if (isPopulatingSavedCreditRatings.value) return;

  const rating = formData.value.credit_ratings[index];

  if (
    !rating?.rating ||
    !rating?.rating_source_link ||
    !rating?.rating_added_date
  ) {
    return;
  }

  await handleCreditRatingUpdate(index);
};
const addMoreRating = () => {
  const selectedAgencies = new Set(
    formData.value.credit_ratings.map((r) => r.agency),
  );
  const remainingAgencies = agencyOptions.value.filter(
    (agency) => !selectedAgencies.has(agency.name),
  );

  if (remainingAgencies.length > 0) {
    formData.value.credit_ratings.push({
      agency: null,
      rating: null,
      ranking: null,
      available: null,
      rating_source_link: "",
      rating_added_date: null,
    });
  }
};

const onUpdateCreditRatingApplicable = async (isApplicable) => {
  if (isApplicable) return;

  const hadSavedCreditRatings = hasSavedCreditRatings.value;

  formData.value.credit_ratings = [
    {
      agency: null,
      rating: null,
      ranking: null,
      available: null,
      rating_source_link: "",
      rating_added_date: null,
    },
  ];

  if (!hadSavedCreditRatings) return;

  await handleCreditRatingUpdate(0);
};

watch(
  isCreditRatingsApplicable,
  (isApplicable) => {
    if (!isApplicable) {
      formData.value.credit_ratings = [
        {
          agency: null,
          rating: null,
          ranking: null,
          available: null,
          rating_source_link: "",
          rating_added_date: null,
        },
      ];
    }
  },
  {
    immediate: true,
  },
);

const transformRatingsData = (ratings) => {
  return ratings.map((item) => {
    const [agency, details] = Object.entries(item)[0];

    return { agency: agency?.toUpperCase(), ...details };
  });
};

watch(
  () => unref(user)?.credit_ratings,
  async (value) => {
    if (value?.length) {
      isPopulatingSavedCreditRatings.value = true;

      isCreditRatingsApplicable.value = true;

      try {
        await nextTick();

        formData.value.credit_ratings = transformRatingsData(value);

        await nextTick();
      } finally {
        isPopulatingSavedCreditRatings.value = false;
      }
    }
  },
  { deep: true, immediate: true },
);

const initCreditRatingsFilter = async () => {
  const response = await getCreditAgencies();

  ratingAgencies.value = response;
};

const legalCasesOptions = ref([
  {
    name: "Found",
    value: "found",
  },
  {
    name: "Not Found",
    value: "not found",
  },
]);

const eligibilityOptions = ref([
  {
    name: "Eligible",
    code: "E",
    value: true,
  },
  {
    name: "Not Eligible",
    code: "NE",
    value: false,
  },
  {
    name: "Inconclusive",
    code: "NC",
    value: false,
  },
]);

const approvalInfo = computed(() => {
  const status = unref(user)?.evaluation_details?.status_cd;

  const approversName = getApproversName(status);
  const approversRemarks = getApproversRemarks();

  return [
    {
      label: "Approvers:",
      value: approversName,
    },
    {
      label: "Remarks:",
      value: approversRemarks,
    },
  ];
});

const overdraftInfo = (overdraft) => {
  let infoList = [];

  for (const value of overdraft) {
    infoList.push([
      {
        label: "Account Name:",
        value: value.account_name,
      },
      {
        label: "Current Balance:",
        value: value.balance,
      },
    ]);
  }

  return infoList;
};

const getApproversName = (status) => {
  const approvers = unref(user)?.evaluation_details?.approvers || [];

  if (status === "awaiting_approval") {
    const names = approvers.map((item) => item.name);

    return names.join(", ");
  } else if (status === "approved" || status === "rejected") {
    const names = approvers
      .filter((item) => item.status_cd === status)
      .map((item) => item.name);

    return names.join(", ");
  } else return "";
};

const getApproversRemarks = () => {
  const approvers = unref(user)?.evaluation_details?.approvers || [];

  return approvers?.[0]?.remarks;
};

const computedExposureEligibility = computed({
  get() {
    if (unref(formData).credit_eligibility) {
      return unref(formData).credit_eligibility;
    }

    return null;
  },
  set(value) {
    unref(formData).credit_eligibility = unref(eligibilityOptions).find(
      (opt) => opt.code == value,
    );

    if (!unref(formData).credit_eligibility?.value) {
      unref(formData).credit_limit = 1;
    }
  },
});

const isSubmitDisabled = computed({
  get() {
    const formDataValue = unref(formData);

    return !!(
      !formDataValue.legal_cases ||
      !formDataValue.credit_limit ||
      !formDataValue.credit_eligibility ||
      !formDataValue.direct_indirect_grade ||
      (unref(isCreditRatingsApplicable) &&
        (!formDataValue.credit_ratings?.length ||
          !formDataValue.credit_ratings.every(
            (rating) =>
              rating?.rating &&
              rating?.rating_source_link &&
              rating?.rating_added_date,
          )))
    );
  },
});

const handleSendReportForApproval = () => {
  $q.dialog({
    component: SendReportDialogForm,
    componentProps: {
      creditSummary: unref(formData),
    },
  });
};

const handleStatusOfCR = (status) => {
  $q.dialog({
    component: UpdateCRStatusDialogForm,
    componentProps: {
      status: status,
      creditLimit: unref(formData).credit_limit,
      approversList: unref(user).evaluation_details?.approvers,
    },
  });
};

const submitButton = computed(() => {
  return {
    label: "Send report for approval",
    disable: unref(isSubmitDisabled),
    onClick: () => handleSendReportForApproval(),
  };
});

const rejectCRButton = computed(() => {
  return {
    label: "Reject CR Report",
    color: "error-70",
    outline: true,
    disabled: unref(formData)?.credit_eligibility?.value,
    onClick: () => handleStatusOfCR("rejected"),
  };
});

const approveCRButton = computed(() => {
  return {
    label: "Approve CR Report",
    disabled: !unref(formData)?.credit_eligibility?.value,
    onClick: () => handleStatusOfCR("approved"),
  };
});

const hasValue = (val) => val !== null && val !== undefined && val !== "";

const isRatingProvided = computed(() => {
  return (
    unref(isCreditRatingsApplicable) &&
    Array.isArray(formData.value?.credit_ratings) &&
    formData.value.credit_ratings.some((rating) => hasValue(rating?.rating))
  );
});

const hasSavedCreditRatings = computed(() => {
  return (
    Array.isArray(user.value?.credit_ratings) &&
    user.value.credit_ratings.length > 0
  );
});

const isCompleteNowDisabled = computed(() => {
  const legalCase =
    formData.value?.legal_cases ??
    user.value?.creditSummary?.background_check?.legal_cases;

  if (isRatingProvided.value) {
    return false;
  }

  return !hasValue(legalCase);
});

const initialLegalCase = ref(null);
const hasCapturedInitialLegalCase = ref(false);

watch(
  () => user.value?.creditSummary?.background_check?.legal_cases,
  (val) => {
    if (!hasCapturedInitialLegalCase.value) {
      initialLegalCase.value = val || "";

      hasCapturedInitialLegalCase.value = true;
    }
  },
  { immediate: true },
);

const handleLegalCaseChange = async (legal_case) => {
  try {
    await putLegalCaseStatus({
      user_id: user.value?._id,
      legal_case,
    });

    user.value.creditSummary.background_check.legal_cases = legal_case;

    if (isDirectIndirectCompleted.value) {
      await refreshCreditEvaluationSummary();
    }
  } catch (err) {
    console.error(err);
  }
};

const editDirector = (director) => {
  toggleDirectorForm({
    open: true,
    level: 0,
    props: {
      director,
      onDirectorCreditScoreUpdated: handleDirectorCreditScoreUpdated,
    },
  });
};

const areAllDirectorCreditScoresAdded = computed(() => {
  const directors = user.value?.businessDetails?.directors || [];

  if (!directors.length) return false;

  return directors.every((director) => hasValue(director?.credit_score));
});
const refreshCreditEvaluationSummary = async () => {
  if (!user.value?._id || isRefreshingCreditEvaluation.value) return;

  try {
    isRefreshingCreditEvaluation.value = true;

    await getUserCreditVerification({
      params: {}, // no stage_name
    });
  } catch (error) {
    console.error(error);
  } finally {
    isRefreshingCreditEvaluation.value = false;
  }
};

const handleDirectorCreditScoreUpdated = async () => {
  await nextTick();

  if (
    isDirectIndirectCompleted.value &&
    areAllDirectorCreditScoresAdded.value
  ) {
    await refreshCreditEvaluationSummary();
  }
};

onBeforeMount(async () => {
  await getUser(unref(user)?._id);

  await initCreditRatingsFilter();
});
</script>
